import EmptyListingState from "@/components/dashboard/listings/EmptyListingState";
import ListingGridView from "@/components/dashboard/listings/ListingGridView";
import ListingListView from "@/components/dashboard/listings/ListingListView";
import { BoostListingDialog } from "@/components/property/BoostListingDialog";
import { FeaturedUpgradeDialog } from "@/components/property/FeaturedUpgradeDialog";
import { listingsService } from "@/lib/api/services/listings";
import {
  publishUserListing,
  deleteUserListing,
  updateUserListing, // Import updateUserListing
} from "@/lib/api/listings/userListingsApi"; // Import userListingsApi functions
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Listing } from "@/lib/types"; // Import the actual Listing type
import { formatCurrency } from "@/lib/formatters"; // Import formatCurrency
import ListingTable from "@/components/dashboard/listings/ListingTable"; // Import ListingTable
import { UserRole } from "@/lib/types"; // Import UserRole

// Define the expected ListingType for frontend display
interface DisplayListingType {
  id: string;
  title: string;
  address: string;
  price: string;
  type: string; // e.g., "Office Space", "Retail Space"
  status: string;
  image: string; // URL of the primary image
  featured: boolean;
  boosted: boolean;
  views: number; // Added views
  inquiries: number; // Added inquiries
}

// Helper function to map backend Listing to frontend DisplayListingType
const mapListingToDisplayType = (listing: Listing): DisplayListingType => {
  const primaryImage =
    listing.images?.find((img) => img.is_primary) || listing.images?.[0];
  const imageUrl = primaryImage?.url || "/placeholder-property.avif"; // Use a placeholder if no image

  return {
    id: String(listing.id),
    title: listing.title,
    address: listing.address || "N/A", // Provide a default for address
    price: formatCurrency(listing.price, listing.currency),
    type: listing.propertyType?.name || listing.listing_type || "N/A", // Use propertyType.name if available
    status: listing.status.charAt(0).toUpperCase() + listing.status.slice(1), // Capitalize status
    image: imageUrl,
    featured: !!listing.is_featured, // Convert to boolean
    boosted: !!listing.is_boosted, // Convert to boolean
    views: listing.views || 0, // Map views, default to 0 if undefined
    inquiries: 0, // Inquiries not directly available in Listing, default to 0
  };
};

interface ListingsContentProps {
  listings: Listing[]; // Now accepts the raw Listing type
  searchQuery: string;
  displayMode: "list" | "grid";
  onClearSearch: () => void;
  userRole?: UserRole; // Add userRole prop
  onPublish: (id: number) => void; // Add onPublish prop
  onDelete: (id: number) => void; // Add onDelete prop
  onFeatureToggle: (id: number) => void; // Add onFeatureToggle prop
  isPublishing: boolean; // Add isPublishing prop
  isDeleting: boolean; // Add isDeleting prop
  isFeatureToggling: boolean; // Add isFeatureToggling prop
}

const ListingsContent = ({
  listings,
  searchQuery,
  displayMode,
  onClearSearch,
  userRole,
  onPublish,
  onDelete,
  onFeatureToggle,
  isPublishing,
  isDeleting,
  isFeatureToggling,
}: ListingsContentProps) => {
  const [selectedListingId, setSelectedListingId] = useState<string | null>(
    null
  );
  const [showFeaturedDialog, setShowFeaturedDialog] = useState(false);
  const [showBoostDialog, setShowBoostDialog] = useState(false);
  const queryClient = useQueryClient();

  // Mutation for toggling is_featured

  const boostMutation = useMutation({
    mutationFn: ({
      listingId,
      boostOption,
    }: {
      listingId: string;
      boostOption: "30min" | "6h" | "24h";
    }) => listingsService.boost(Number(listingId), { boostOption }),
    onSuccess: (data, variables) => {
      toast.success(`Listing ${variables.listingId} boosted successfully!`);
      setShowBoostDialog(false);
      setSelectedListingId(null);
      queryClient.invalidateQueries({ queryKey: ["dashboardListings"] });
      queryClient.invalidateQueries({
        queryKey: ["listing", variables.listingId.toString()],
      });
    },
    onError: (error) => {
      console.error("Error boosting listing:", error);
      const message =
        (error as any)?.response?.data?.message ||
        "Failed to boost listing. Please try again.";
      toast.error(message);
    },
  });

  const featureMutation = useMutation({
    mutationFn: ({
      listingId,
      featureOption,
    }: {
      listingId: string;
      featureOption: "1d" | "7d" | "30d";
    }) => listingsService.feature(Number(listingId), { featureOption }),
    onSuccess: (data, variables) => {
      toast.success(`Listing ${variables.listingId} featured successfully!`);
      setShowFeaturedDialog(false);
      setSelectedListingId(null);
      queryClient.invalidateQueries({ queryKey: ["dashboardListings"] });
      queryClient.invalidateQueries({
        queryKey: ["listing", variables.listingId.toString()],
      });
    },
    onError: (error) => {
      console.error("Error featuring listing:", error);
      const message =
        (error as any)?.response?.data?.message ||
        "Failed to feature listing. Please try again.";
      toast.error(message);
    },
  });

  const handleUpgradeToFeatured = (featureOption: "1d" | "7d" | "30d") => {
    if (selectedListingId) {
      featureMutation.mutate({ listingId: selectedListingId, featureOption });
    } else {
      toast.error("No listing selected to feature.");
    }
  };

  const handleBoostListing = (boostOption: "30min" | "6h" | "24h") => {
    if (selectedListingId) {
      boostMutation.mutate({ listingId: selectedListingId, boostOption });
    } else {
      toast.error("No listing selected to boost.");
    }
  };

  const openFeaturedDialog = (id: string) => {
    setSelectedListingId(id);
    setShowFeaturedDialog(true);
  };

  const openBoostDialog = (id: string) => {
    setSelectedListingId(id);
    setShowBoostDialog(true);
  };

  const getSelectedPropertyTitle = () => {
    // Find the listing using the original Listing type
    return listings.find((listing) => listing.id === Number(selectedListingId))
      ?.title;
  };

  if (listings.length === 0) {
    return (
      <EmptyListingState
        searchQuery={searchQuery}
        onClearSearch={onClearSearch}
      />
    );
  }

  // Map listings to DisplayListingType for grid/list view, but pass original for table view
  const displayListings = listings.map(mapListingToDisplayType);

  return (
    <>
      {displayMode === "grid" ? (
        <ListingGridView
          listings={displayListings} // Pass mapped listings
          onOpenFeaturedDialog={openFeaturedDialog}
          onOpenBoostDialog={openBoostDialog}
        />
      ) : displayMode === "list" ? (
        <ListingTable
          listings={listings}
          userRole={userRole}
          onPublish={onPublish}
          onDelete={onDelete}
          onFeatureToggle={onFeatureToggle} // Pass the new handler
          isPublishing={isPublishing} // Pass isPublishing
          isDeleting={isDeleting} // Pass isDeleting
          isFeatureToggling={isFeatureToggling} // Pass isFeatureToggling
        />
      ) : (
        <ListingListView
          listings={displayListings} // Pass mapped listings
          onOpenFeaturedDialog={openFeaturedDialog}
          onOpenBoostDialog={openBoostDialog}
        />
      )}

      <FeaturedUpgradeDialog
        open={showFeaturedDialog}
        onOpenChange={setShowFeaturedDialog}
        onUpgrade={handleUpgradeToFeatured}
        propertyTitle={getSelectedPropertyTitle()}
        isUpgrading={featureMutation.isPending}
      />

      <BoostListingDialog
        open={showBoostDialog}
        onOpenChange={setShowBoostDialog}
        onBoost={handleBoostListing}
        propertyTitle={getSelectedPropertyTitle()}
        isBoosting={boostMutation.isPending}
      />
    </>
  );
};

export default ListingsContent;
